import { useState } from 'react'
import { Search, Clock, Mail, FileText, Bell, MessageSquare } from 'lucide-react'

type SimulationMessage = {
  id?: string | number
  from?: string
  to?: string
  subject?: string
  body?: string
  description?: string
  timestamp?: string
  elapsedTime?: string
  priority?: 'HIGH' | 'MEDIUM' | 'LOW'
  messageType?: string
  type?: string
}

type ReviewMessageTimelineProps = {
  messages: unknown[]
}

function getMessageIcon(type: string | undefined) {
  switch (type?.toLowerCase()) {
    case 'email':
      return <Mail className="w-4 h-4" />
    case 'report':
      return <FileText className="w-4 h-4" />
    case 'notification':
      return <Bell className="w-4 h-4" />
    default:
      return <MessageSquare className="w-4 h-4" />
  }
}

function getPriorityColor(priority: string | undefined) {
  switch (priority?.toUpperCase()) {
    case 'HIGH':
      return 'border-l-red-500 bg-red-50'
    case 'MEDIUM':
      return 'border-l-yellow-500 bg-yellow-50'
    case 'LOW':
      return 'border-l-green-500 bg-green-50'
    default:
      return 'border-l-gray-300 bg-gray-50'
  }
}

export function ReviewMessageTimeline({ messages }: ReviewMessageTimelineProps) {
  const [searchQuery, setSearchQuery] = useState('')

  const typedMessages = messages as SimulationMessage[]

  const filteredMessages = typedMessages.filter(msg => {
    if (!searchQuery || searchQuery.length < 2) return true

    const lowerQuery = searchQuery.toLowerCase()
    const searchableText = [
      msg.subject,
      msg.body,
      msg.description,
      msg.from,
      msg.to,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return searchableText.includes(lowerQuery)
  })

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search messages..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Messages list */}
      <div className="flex-1 overflow-auto p-4 space-y-3">
        {filteredMessages.length > 0 ? (
          filteredMessages.map((msg, index) => (
            <div
              key={msg.id || index}
              className={`border-l-4 rounded-r-lg p-4 ${getPriorityColor(msg.priority)}`}
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">
                    {getMessageIcon(msg.messageType || msg.type)}
                  </span>
                  <span className="font-medium text-gray-900">
                    {msg.subject || msg.description || 'Message'}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  <span>{msg.timestamp || msg.elapsedTime || '—'}</span>
                </div>
              </div>

              {/* From/To */}
              {(msg.from || msg.to) && (
                <div className="text-xs text-gray-500 mb-2">
                  {msg.from && <span>From: {msg.from}</span>}
                  {msg.from && msg.to && <span> &rarr; </span>}
                  {msg.to && <span>To: {msg.to}</span>}
                </div>
              )}

              {/* Body */}
              {msg.body && (
                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                  {msg.body}
                </div>
              )}

              {/* Priority badge */}
              {msg.priority && (
                <div className="mt-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      msg.priority === 'HIGH'
                        ? 'bg-red-100 text-red-700'
                        : msg.priority === 'MEDIUM'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {msg.priority}
                  </span>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            {searchQuery
              ? 'No messages match your search'
              : 'No messages in this simulation'}
          </div>
        )}
      </div>
    </div>
  )
}
